import { AnimationOptions, useAnimate, motion } from "motion/react";
import { Location } from "../../../providers/routing/types";
import {
  bottomContainer,
  mainContainer,
  navigationMenu,
  navigationMenuContainer,
  revealingContainer,
} from "./style.css";
import { RefObject, useImperativeHandle } from "react";
import { NavigationLink } from "./navigation-link";
import {
  ABOUT_US_PAGE,
  ADVANCED_TECHNOLOGY_PAGE,
  COST_YOUR_PROJECT_PAGE,
  CUSTOM_DESIGN_PAGE,
  CUTTING_COSTS_PAGE,
  NEWS_PAGE,
  SUSTAINABILITY_PAGE,
} from "../../../providers/routing/routes";
import text from "./resources/text.json";
import { getUrl } from "../../utils/getUrl";
import { useLanguage } from "../../../providers/language/context";
import { graphql, useStaticQuery } from "gatsby";

export type NavigationPanelRef = {
  hide: () => Promise<void>;
  open: () => Promise<void>;
};

type NavigationPanelProps = {
  onNavigate?: (location: Location) => void;
  currentLocation: Location;
  animationRef?: RefObject<NavigationPanelRef>;
};

const animationOptions: AnimationOptions = {
  duration: 0.6,
  ease: "easeInOut",
};

export const NavigationPanel = ({
  onNavigate,
  currentLocation,
  animationRef,
}: NavigationPanelProps) => {
  const { language } = useLanguage();
  const [scope, animate] = useAnimate();

  const {
    advancedTechnology,
    customDesign,
    cuttingCosts,
    aboutUs,
    costProject,
    sustainability,
  } = text[language as keyof typeof text];

  const textQuery = useStaticQuery(graphql`
    query {
      allPagesJson {
        nodes {
          title
          language
          fields {
            bodyParsed {
              links {
                LINKED_IN
              }
            }
          }
        }
      }
    }
  `);

  const textResource =
    textQuery.allPagesJson.nodes.find(
      (i: Record<string, string | number>) =>
        i?.language === language && i?.title === "Footer",
    ) ??
    textQuery.find(
      (i: Record<string, string | number>) =>
        i?.language === "en" && i?.title === "Footer",
    );
  const { LINKED_IN = "" } = textResource?.fields?.bodyParsed?.links ?? {};

  useImperativeHandle(
    animationRef,
    () => ({
      hide: async () => {
        await animate(
          scope.current,
          {
            clipPath: "inset(0 0 100% 0)",
          },
          animationOptions,
        );
        scope.current.style.height = "0";
      },
      open: async () => {
        scope.current.style.height = "100%";
        await animate(
          scope.current,
          {
            clipPath: "inset(0 0 0 0)",
          },
          animationOptions,
        );
      },
    }),
    [],
  );

  const handleNavigate = (location: Location) => () => {
    onNavigate?.(location);
  };

  return (
    <motion.div
      ref={scope}
      initial={{
        clipPath: "inset(0 0 100% 0)",
      }}
      transition={{
        duration: 0.6,
        ease: "easeInOut",
      }}
      className={revealingContainer}
    >
      <div className={navigationMenuContainer}>
        <div className={navigationMenu}>
          <div className={mainContainer}>
            <NavigationLink
              to={getUrl(language, ADVANCED_TECHNOLOGY_PAGE)}
              isActive={currentLocation === "inner-technology"}
              onClick={handleNavigate("inner-technology")}
            >
              {advancedTechnology}
            </NavigationLink>
            <NavigationLink
              to={getUrl(language, CUTTING_COSTS_PAGE)}
              isActive={currentLocation === "inner-cutting-costs"}
              onClick={handleNavigate("inner-cutting-costs")}
            >
              {cuttingCosts}
            </NavigationLink>
            <NavigationLink
              to={getUrl(language, SUSTAINABILITY_PAGE)}
              isActive={currentLocation === "inner-sustainability"}
              onClick={handleNavigate("inner-sustainability")}
            >
              {sustainability}
            </NavigationLink>
            <NavigationLink
              to={getUrl(language, CUSTOM_DESIGN_PAGE)}
              isActive={currentLocation === "inner-custom-design"}
              onClick={handleNavigate("inner-custom-design")}
            >
              {customDesign}
            </NavigationLink>
            {/* <NavigationLink
              to={getUrl(language, NEWS_PAGE)}
              isActive={currentLocation === "inner-news"}
              onClick={handleNavigate("inner-news")}
            >
                {news}
            </NavigationLink> */}
            <NavigationLink
              to={getUrl(language, ABOUT_US_PAGE)}
              isActive={currentLocation === "inner-about-us"}
              onClick={handleNavigate("inner-about-us")}
            >
              {aboutUs}
            </NavigationLink>
            <NavigationLink
              to={getUrl(language, COST_YOUR_PROJECT_PAGE)}
              onClick={handleNavigate("cost-your-project-now")}
            >
              {costProject}
            </NavigationLink>
          </div>
          <div className={bottomContainer}>
            <NavigationLink isExternal to={LINKED_IN}>
              LinkedIn
            </NavigationLink>
            {/*<NavigationLink isExternal to={YOUTUBE}>*/}
            {/*  YouTube*/}
            {/*</NavigationLink>*/}
            {/*<NavigationLink isExternal to={TIKTOK}>*/}
            {/*  TikTok*/}
            {/*</NavigationLink>*/}
            {/*<NavigationLink isExternal to={INSTAGRAM}>*/}
            {/*  Instagram*/}
            {/*</NavigationLink>*/}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
